/**
 * P2-10 · Storefront listing defaults.
 *
 * `template:core` constants consumed by the listing query (P2-10) and its UI
 * consumers (P2-09/11/12/13). Per-brand overrides can move these into
 * BrandSettings later without changing call sites.
 */

/** Default products per PLP page. */
export const PLP_DEFAULT_PAGE_SIZE = 24;

/** Hard cap on requested page size — keeps a hostile query bounded. */
export const PLP_MAX_PAGE_SIZE = 60;

/**
 * Hard cap on the requested page NUMBER (P2-14 hardening): listing reads are
 * cached per full input, so an unbounded `page` lets a bot mint one persistent
 * cache entry per request. 1000 pages × max page size comfortably exceeds any
 * real category; deep links beyond it clamp (the UI clamps to the actual last
 * page after the read anyway).
 */
export const PLP_MAX_PAGE = 1000;

/** A product published within this many days gets the "new" badge. */
export const NEW_BADGE_DAYS = 30;

/**
 * At or below this many units of total stock (while still in stock), the card
 * shows the "Only N left" badge (P2-04, threshold per P2-DB04 §02).
 */
export const LOW_STOCK_THRESHOLD = 5;

/** Colour swatches shown on a card before collapsing into "+X more". */
export const CARD_MAX_SWATCHES = 4;

/**
 * P3-08 · Quantity cap for a pre-order/backorderable variant — the units a
 * shopper can add beyond physical stock in one go. In-stock variants are capped
 * by their own `stock` instead; this only bounds the backorder path. Enforced
 * both in the stepper and server-side in `cart.addItem`.
 */
export const CART_BACKORDER_CAP = 99;

/**
 * P3-08 · Where buy-now (and, in P4, the cart) routes to. The checkout route
 * itself lands in **P4**; the `pdpBuyNow` flag stays off until then so nobody
 * is sent to a not-yet-built page.
 */
export const CHECKOUT_PATH = "/checkout";

/**
 * P4-15 · Where Stripe redirects after `confirmPayment` (the PaymentIntent
 * `return_url`). A neutral "confirming your payment" page — it never asserts the
 * order is paid; the signature-verified webhook (P4-16) is the source of truth.
 */
export const CHECKOUT_PROCESSING_PATH = "/checkout/processing";

/**
 * P4-21 · The post-purchase confirmation page for an order. `token` is the
 * signed order-access token — it authorises guests (and email links) to view
 * exactly this order; the page also accepts the signed-in purchaser without it.
 */
export function checkoutConfirmationPath(orderId: string, token?: string): string {
  const base = `/checkout/confirmation/${orderId}`;
  return token ? `${base}?t=${encodeURIComponent(token)}` : base;
}

/**
 * P4-02 · The full cart page route (the mini-cart's "View cart" link). The page
 * itself lands in **P4-03**; the link exists now so the drawer is complete.
 */
export const CART_PATH = "/cart";

/**
 * Option names treated as the colour axis for card swatches, matched
 * case-insensitively. Option names are brand data (P1-01), so this is a
 * recognition heuristic, not an enum of allowed values.
 */
export const COLOUR_OPTION_NAMES = ["colour", "color"];

/**
 * P3-05 · `swatch.*` presentation tokens for the PDP colour swatches
 * (P3-DB02 §Brand-configurable). `template:brandable` — core defaults here
 * until the knobs move into BrandSettings without changing call sites.
 * - `shape`: round vs squared swatch.
 * - `size`: swatch diameter preset (all keep a ≥44px touch target).
 * - `unavailableStyle`: how out-of-stock colours read — dimmed or struck.
 */
export interface SwatchConfig {
  shape: "circle" | "square";
  size: "sm" | "md" | "lg";
  unavailableStyle: "dim" | "strike";
}

export const SWATCH_CONFIG: SwatchConfig = {
  shape: "circle",
  size: "md",
  unavailableStyle: "strike",
};

/**
 * P3-13 · Complementary items in a frequently-bought-together bundle (the
 * anchor is shown on top of these). Kept small so the bundle stays scannable.
 */
export const FBT_MAX_ITEMS = 3;

/** P3-13 · Max accessory cards in the PDP accessories rail. */
export const ACCESSORIES_LIMIT = 12;

/**
 * P4-05 · Cross-sell cards per cart surface (P4-DB01 open question). The
 * mini-cart is a narrow drawer, so it stays compact; the full cart page has
 * room for a wider row. Brand-configurable later; these are the defaults.
 */
export const CART_CROSS_SELLS_MINI = 3;
export const CART_CROSS_SELLS_FULL = 6;

/**
 * P3-14 · Product rows in the search-overlay suggestions. The P3-DB04 contract
 * allows 4–6 per brand (`brand.search.productCount`); this is the core default
 * until that knob moves into BrandSettings.
 */
export const SEARCH_PRODUCT_SUGGESTIONS = 5;

/**
 * P8-13 · Posts per page on the blog index and the tag/author archives.
 *
 * Lives here rather than in the route file because a Next page module may only
 * export its own reserved fields — an exported constant there is a build error,
 * not a type error, so it survives `tsc` and dies in `next build`. One value so
 * the index and the archives paginate identically.
 */
export const BLOG_PAGE_SIZE = 12;

/**
 * P2-15 · Blocks in the first N page positions always render eagerly — they
 * are (or can be) above the fold on every viewport, so deferring them would
 * hurt LCP. Below this index, a block's registry `eager` hint decides
 * (hero/richText stay eager anywhere; data blocks defer).
 */
export const EAGER_BLOCK_COUNT = 2;

/**
 * P2-15 · `perf.lazyBelowFold` — when true (default), below-the-fold blocks
 * render inside a lazy boundary (Suspense-streamed with a space-reserving
 * skeleton + browser-native deferred rendering). Escape hatch for debugging;
 * not a per-brand setting.
 */
export const LAZY_BELOW_FOLD = true;
